/**
 * The canonical internal estimate detail page.
 *
 * One page renders estimates from every origin — built in CIS estimating,
 * authored natively in Tharstern, or a merchandise draft — and its
 * job is to make a CSR's trip into Tharstern unnecessary. Every panel is
 * driven by the `coverage` / `status` / `provenance` discriminator on its
 * block, and the one rule that outranks layout: **a figure the system does not
 * hold is never rendered as a number.** No `?? 0`, no bare em-dash standing in
 * for a price. A missing value renders as a stated gap with its reason in an
 * ⓘ, because a fabricated £0.00 on a pricing surface reads as fact, and a
 * wrong number here is worse than an honest absence.
 *
 * Laid out as an object page (docs/features/journey-visual-language/
 * 01_proposal.md §4, and the rules in 02_estimate_page_adoption.md): the
 * header with its two pills, key facts and at most two buttons (the rest in
 * ⋯), the lifecycle rail in its own card, then tabs — Now (the default),
 * Lines, Price, Delivery, Billing and Activity — each at its own
 * address and each one container. A tab is left out only when it can never
 * apply to the kind of estimate; otherwise it states its own emptiness.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Link,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from "react-router";
import { isFeatureEnabled } from "@shared-core/config/features";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../app/components/primitives/alert-dialog";
import { Button } from "../app/components/primitives/button";
import { Card, CardContent } from "../app/components/primitives/card";
import { Badge } from "../app/components/primitives/badge";
import { Textarea } from "../app/components/primitives/textarea";
import { ArrowLeft, Loader2, Unlink } from "lucide-react";
import { LifecycleRail } from "../app/components/patterns/LifecycleRail";
import {
  ObjectHeader,
  type Crumb,
  type KeyFact,
} from "../app/components/patterns/ObjectHeader";
import {
  ObjectTabs,
  type ObjectTab,
} from "../app/components/patterns/ObjectTabs";
import { NowPanel } from "../app/components/patterns/NowPanel";
import { InfoTip } from "../app/components/patterns/InfoTip";
import type { EstimateDetailViewItem } from "@shared-core/data/api-types";
import { EstimateControls } from "../app/components/domain/estimates/EstimateControls";
import {
  ESTIMATE_TABS,
  RETIRED_ESTIMATE_TABS,
  isRouteEnabled,
  paths,
  type EstimateTab,
} from "@shared-core/routing/routes";
import {
  formatCurrencyGbp,
  formatDateTimeOrDash,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import {
  railFromDraftEstimate,
  railFromTharsternEstimate,
} from "@shared-core/domain/estimate-lifecycle-rail";
import {
  estimateOriginPresentation,
  estimateStatusLabel,
  estimateStatusTone,
  linkedJobInvoicePresentation,
  type LinkedJobInvoicePresentation,
} from "@shared-core/domain/estimate-presentation";
import { useSmartBack } from "../app/useSmartBack";
import type { MerchandiseDraftEstimate } from "@shared-core/data/merchandise-types";
import {
  DRAFT_ESTIMATE_STATE_LABELS,
  draftEstimateLifecycleDetail,
  draftEstimateOutcome,
  draftEstimateStateTone,
} from "@shared-core/domain/merchandise-presentation";
import {
  DraftEstimateCostChangedBanner,
  type DraftEstimateBusyAction,
} from "../app/components/domain/merchandise/DraftEstimateCostChangedBanner";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { ObjectChip } from "../app/components/patterns/ObjectChip";
import { formatDateOnly, formatValidUntil, Gap } from "./estimate-detail/atoms";
import { DetailsTab } from "./estimate-detail/DetailsTab";
import { LinesTab, WhatWasAskedFor } from "./estimate-detail/LinesTab";
import { DraftPriceTab, PriceTab } from "./estimate-detail/PriceTab";
import { DeliveryTab } from "./estimate-detail/DeliveryTab";
import { BillingTab } from "./estimate-detail/BillingTab";
import { ActivityTab } from "./estimate-detail/ActivityTab";
import { estimateNowItems } from "./estimate-detail/EstimateNow";

const TAB_LABELS: Record<EstimateTab, string> = {
  now: "Now",
  lines: "Lines",
  price: "Price",
  delivery: "Delivery",
  billing: "Billing",
  details: "Details",
  activity: "Activity",
};

/**
 * The job this estimate became, as one row at the foot of Now: the job (a
 * real document, so a chip), its booked value and date, and its invoice
 * status. A figure the job has not synced yet says so — never £0.00 (#1995,
 * finding 82).
 */
function JobRow({
  relatedJob,
  relatedJobInvoice,
}: {
  relatedJob: NonNullable<EstimateDetailViewItem["linked_job"]>;
  relatedJobInvoice: LinkedJobInvoicePresentation;
}) {
  const jobLabel = `Job ${relatedJob.job_no}`;
  return (
    <section aria-label={jobLabel} className="space-y-2">
      <h3 className="text-sm font-medium text-muted-foreground">Job</h3>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        {/* A job is a real document: a chip, with ↗ when its page can open. */}
        <ObjectChip
          label={jobLabel}
          to={
            isRouteEnabled("jobWorkspace")
              ? paths.jobWorkspace({ jobNo: relatedJob.job_no })
              : undefined
          }
        />
        <span aria-hidden className="text-muted-foreground">
          ·
        </span>
        {relatedJob.booked_value == null ? (
          <Gap>booked value not yet synced</Gap>
        ) : (
          <span>booked {formatCurrencyGbp(relatedJob.booked_value, 2)}</span>
        )}
        <span aria-hidden className="text-muted-foreground">
          ·
        </span>
        {formatDateOnly(relatedJob.ordered_at) ?? (
          <Gap>booking date not yet synced</Gap>
        )}
        <span aria-hidden className="text-muted-foreground">
          ·
        </span>
        <span className="inline-flex items-center gap-1.5">
          invoice:
          <Badge
            variant={
              relatedJobInvoice.kind === "invoiced" ? "success" : "warning"
            }
          >
            {relatedJobInvoice.kind === "invoiced" ? "Invoiced" : "Pending"}
          </Badge>
        </span>
      </p>
    </section>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

type LoadState =
  | { kind: "loading" }
  | {
      kind: "loaded";
      detail: EstimateDetailViewItem;
    }
  | { kind: "missing" }
  | { kind: "error" };

export function EstimateDetailPage() {
  const { id, tab: rawTab } = useParams<{ id: string; tab?: string }>();
  // No segment is Now; a retired tab redirects to its new home below; a
  // segment that names no tab renders a stated gap.
  const retiredTab = rawTab ? (RETIRED_ESTIMATE_TABS[rawTab] ?? null) : null;
  const requestedTab: EstimateTab | null = !rawTab
    ? "now"
    : (ESTIMATE_TABS as readonly string[]).includes(rawTab)
      ? (rawTab as EstimateTab)
      : null;
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useSmartBack(paths.estimates());
  const adapter = useDataAdapter();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  // Re-reads the estimate after a real write (Record outcome / Park & chase,
  // or a draft's own write) so the badge, the Now rows and the
  // controls' own gating visibly reflect it — a write that succeeded must
  // never look like nothing happened. Does not reset to the loading skeleton
  // first: the page already has a good detail on screen, and a write that
  // succeeded must not make it briefly vanish.
  const refetchEstimate = useCallback(() => {
    if (!id) return;
    adapter
      .getEstimateDetail(id)
      .then((detail) => {
        if (detail) setState({ kind: "loaded", detail });
      })
      .catch((err: unknown) => {
        // The write already succeeded — a refresh hiccup must not make the
        // estimate look like it vanished or failed. Keep showing the
        // pre-write snapshot; the next navigation picks up the change.
        console.error("estimates.detail.refetch_failed", err);
      });
  }, [id, adapter]);
  // The merchandise draft estimate behind this id, when it is one. It arrives
  // composed on the estimate detail itself (`draft_estimate`), never on a call
  // of its own — a second fetch here would be the client-side join the rules
  // forbid. Held in state because the draft's own writes return the updated
  // request and the page keeps it without re-reading. Null for every ordinary
  // estimate.
  const [draftEstimate, setDraftEstimate] =
    useState<MerchandiseDraftEstimate | null>(null);
  const [draftEstimateBusy, setDraftEstimateBusy] =
    useState<DraftEstimateBusyAction>(null);
  const [draftEstimateError, setDraftEstimateError] = useState<string>();
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);

  useEffect(() => {
    if (!id) {
      setState({ kind: "missing" });
      return;
    }
    let live = true;
    setState({ kind: "loading" });
    setDraftEstimate(null);
    adapter
      .getEstimateDetail(id)
      .then((detail) => {
        if (!live) return;
        // One response, so a draft can never flash as an ordinary estimate
        // first — the block is either there or the row is not a draft.
        setDraftEstimate(detail?.draft_estimate ?? null);
        setState(detail ? { kind: "loaded", detail } : { kind: "missing" });
      })
      .catch(() => {
        // Distinct from "missing" on purpose: the adapter maps only a 404 to
        // null, so anything reaching here is a real failure (403, 500, network)
        // and telling the CSR the estimate does not exist would be both wrong
        // and unactionable.
        if (live) setState({ kind: "error" });
      });
    return () => {
      live = false;
    };
  }, [id, adapter]);

  // Canonical-URL redirect (audit finding 85): a route param that is not all
  // digits is a uuid, a replica uuid, or the dbt-hash form — every address
  // this page used to be reached by before the Tharstern number could be
  // resolved. Once the estimate has a real number, the address bar should
  // carry it, so the CSR's own URL becomes the one worth bookmarking or
  // pasting into Tharstern. Skipped when the number hasn't synced yet
  // (`estimate_id` empty) — the caller's own address is the only one that
  // exists at that point, and redirecting to an empty id would 404. Also
  // requires the *destination* to be all-digits: `estimate_id` is only ever
  // a plain number in production, but guarding here means a display string
  // that doesn't look like one (rather than a genuine unsynced number) can
  // never send the page chasing its own tail into a redirect loop.
  // The tab goes with it.
  useEffect(() => {
    if (!id || state.kind !== "loaded" || /^\d+$/.test(id)) return;
    const canonicalId = state.detail.estimate_id;
    if (!canonicalId || !/^\d+$/.test(canonicalId)) return;
    navigate(
      paths.estimateDetail({ id: canonicalId, tab: requestedTab ?? undefined }),
      { replace: true, state: location.state },
    );
  }, [id, state, navigate, location.state, requestedTab]);

  if (retiredTab && id) {
    return (
      <Navigate
        to={paths.estimateDetail({ id, tab: retiredTab })}
        replace
        state={location.state}
      />
    );
  }

  if (state.kind === "loading") {
    return (
      <div className="flex items-center justify-center h-64 gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading estimate...
      </div>
    );
  }

  if (state.kind !== "loaded") {
    const isError = state.kind === "error";
    return (
      <div className="space-y-6">
        <BackLink onClick={goBack} />
        <Card className="p-12 text-center">
          <h3 className="mb-2">
            {isError ? "Estimate could not be loaded" : "Estimate not found"}
          </h3>
          <p className="text-muted-foreground">
            {isError
              ? "Something went wrong reading this estimate. It may exist but be outside your portfolio, or the service may be unavailable — try again shortly."
              : "The estimate you're looking for doesn't exist."}
          </p>
        </Card>
      </div>
    );
  }

  const estimate = state.detail;
  const { origin, information, financials, specification, delivery_billing } =
    estimate;
  const relatedJob = estimate.linked_job;
  const originPresentation = estimateOriginPresentation(origin);
  const draftLifecycle = draftEstimate
    ? draftEstimateLifecycleDetail(draftEstimate)
    : null;
  // Tharstern's commercial status, in the words the page has always used:
  // an open estimate reads "Pending".
  const commercialStatusLabel =
    estimate.status === "open"
      ? "Pending"
      : estimateStatusLabel(estimate.status);
  // Tharstern's own estimate number when it has replicated, otherwise the
  // internal id — which is at least addressable in support. Details says
  // which it is, so a reader never takes
  // "est_003" for the estimate number.
  const estimateNumber = estimate.estimate_id || information.estimate_number;
  // A draft estimate has no estimate number until it is sent; its reference
  // is its title.
  const heading = draftEstimate
    ? draftEstimate.reference
    : estimateNumber || estimate.id;
  // Re-price / withdraw / unlink on a draft estimate. Each writes through the
  // adapter, keeps the returned request, and re-reads the estimate so the
  // reporting value and lifecycle badge follow it. There is no send: slice A's
  // terminal state is Priced, and the price card says so in words.
  const runDraftEstimateAction = async (
    action: Exclude<DraftEstimateBusyAction, null>,
    write: (
      request: MerchandiseDraftEstimate,
    ) => Promise<MerchandiseDraftEstimate>,
    fallbackMessage: string,
  ) => {
    if (!draftEstimate) return;
    setDraftEstimateBusy(action);
    setDraftEstimateError(undefined);
    try {
      const updated = await write(draftEstimate);
      setDraftEstimate(updated);
      refetchEstimate();
    } catch (err) {
      // The server's own sentence: a refused reprice, a price the quote
      // cannot support, or a draft another action changed under this one.
      setDraftEstimateError(mutationErrorMessage(err, fallbackMessage));
    } finally {
      setDraftEstimateBusy(null);
    }
  };
  const handleRepriceDraftEstimate = () =>
    runDraftEstimateAction(
      "reprice",
      (request) => adapter.repriceMerchandiseDraftEstimate(request.id),
      "Could not re-price this request — try again shortly.",
    );
  const handleUnlinkDraftEstimateQuote = async () => {
    await runDraftEstimateAction(
      "unlink",
      (request) => adapter.unlinkQuoteFromDraftEstimate(request.id),
      "Could not unlink this quote — try again shortly.",
    );
    setUnlinkConfirmOpen(false);
  };
  const openWithdrawDraftEstimate = () => {
    setDraftEstimateError(undefined);
    setWithdrawConfirmOpen(true);
  };
  const handleWithdrawDraftEstimate = async () => {
    await runDraftEstimateAction(
      "withdraw",
      (request) =>
        adapter.withdrawMerchandiseDraftEstimate(request.id, withdrawReason),
      "Could not withdraw this request — try again shortly.",
    );
    setWithdrawConfirmOpen(false);
    setWithdrawReason("");
  };

  // ── The object page: which tabs this kind of estimate has ────────────────
  // A tab is left out only when it can never apply: a draft has no
  // Tharstern-shaped delivery, billing or notes until it is sent.
  const tabsForKind: readonly EstimateTab[] = draftEstimate
    ? ["now", "details", "lines", "price"]
    : ESTIMATE_TABS;
  const tab =
    requestedTab && tabsForKind.includes(requestedTab) ? requestedTab : null;
  const tabPath = (t: EstimateTab) =>
    paths.estimateDetail({ id: id ?? estimate.id, tab: t });

  // ── Header ───────────────────────────────────────────────────────────────
  const title = draftEstimate
    ? `Merchandise request ${draftEstimate.reference}`
    : `Estimate ${heading}`;
  const crumbs: Crumb[] = estimate.customer
    ? [
        { label: "Customers", to: paths.customers() },
        {
          label: estimate.customer.name,
          to: paths.customerDetail({ customerId: estimate.customer.id }),
        },
        {
          label: "Estimates",
          to: paths.estimates({ customerId: estimate.customer.id }),
        },
        { label: heading },
      ]
    : [{ label: "Estimates", to: paths.estimates() }, { label: heading }];
  // Status, in the estimate's own vocabulary: the draft's, or Tharstern's
  // commercial status.
  const statusPill = draftEstimate ? (
    <Badge variant="neutral">
      {draftEstimateOutcome(draftEstimate)?.label ??
        DRAFT_ESTIMATE_STATE_LABELS[draftEstimate.state]}
    </Badge>
  ) : (
    <Badge variant={estimateStatusTone(estimate.status)}>
      {commercialStatusLabel}
    </Badge>
  );

  // ── Now: what is yours to do, and what it is waiting on ──────────────────
  // The rows come from the backend; the page adds the state of the steps it
  // is taking.
  const nowItems = estimateNowItems(estimate.now, {
    draftBusy: draftEstimateBusy,
    onRepriceDraft: handleRepriceDraftEstimate,
    onWithdrawDraft: openWithdrawDraftEstimate,
  });
  const attentionCount = nowItems.filter(
    (i) => i.tone === "attention" || i.tone === "do",
  ).length;

  // Health, only when there is something to say.
  const healthPill =
    draftEstimate?.state === "cost_changed" ? (
      <Badge variant={draftEstimateStateTone(draftEstimate.state)}>
        {DRAFT_ESTIMATE_STATE_LABELS[draftEstimate.state]}
      </Badge>
    ) : nowItems.some((i) => i.tone === "attention") ? (
      <Badge variant="warning">Needs attention</Badge>
    ) : information.expiry?.is_expired ? (
      <Badge variant="neutral">Expired</Badge>
    ) : null;

  const quotedPence = draftEstimate
    ? (draftEstimate.pricing?.ourPricePence ?? null)
    : financials.quoted_total_pence;
  const spec = specification.summary || estimate.description;
  const originText = draftEstimate
    ? `Merchandise request · created in One ${formatDateTimeOrDash(draftEstimate.createdAt)}`
    : `${originPresentation.label}${
        origin.submitted_at
          ? ` · ${formatDateTimeOrDash(origin.submitted_at)}`
          : ""
      }`;
  // Only facts that have a value: quantity, spec, valid until, origin, and
  // the required-by date when one is set. Details repeats them in the full
  // reference list.
  const facts: KeyFact[] = [
    ...(specification.quantity != null
      ? [
          {
            label: "Quantity",
            value: specification.quantity.toLocaleString("en-GB"),
          },
        ]
      : []),
    ...(spec ? [{ label: "Spec", value: spec }] : []),
    ...(information.expiry?.expires_at
      ? [
          {
            label: "Valid until",
            value: formatValidUntil(information.expiry)!,
          },
        ]
      : []),
    { label: "Origin", value: originText },
    ...(formatDateOnly(delivery_billing.delivery.required_by)
      ? [
          {
            label: "Required by",
            value: formatDateOnly(delivery_billing.delivery.required_by)!,
          },
        ]
      : []),
  ];

  const rail = draftEstimate
    ? railFromDraftEstimate(draftEstimate)
    : railFromTharsternEstimate({
        status: estimate.status,
        estimateDate: estimate.estimate_date || null,
        job: relatedJob
          ? {
              jobNo: relatedJob.job_no,
              orderedAt: relatedJob.ordered_at ?? null,
            }
          : null,
        canLinkJob: isRouteEnabled("jobWorkspace"),
      });

  const tabs: ObjectTab[] = tabsForKind.map((t) => ({
    id: t,
    label: TAB_LABELS[t],
    to: tabPath(t),
    badge:
      t === "now" && attentionCount > 0
        ? { count: attentionCount, tone: "amber" as const }
        : null,
  }));

  return (
    <div key={estimate.id} className="space-y-4">
      <div>
        <BackLink onClick={goBack} />
        <ObjectHeader
          crumbs={crumbs}
          title={title}
          pills={
            <>
              {!draftEstimate && !estimateNumber && (
                // The title falls back to the internal id; this says so.
                <span className="inline-flex items-center gap-1">
                  <Badge variant="outline">
                    Estimate number not synced yet
                  </Badge>
                  <InfoTip label="About the estimate number">
                    Tharstern has not given this estimate its number yet. It
                    arrives with the next sync; until then the page is addressed
                    by its internal id, {estimate.id}.
                  </InfoTip>
                </span>
              )}
              {statusPill}
              {healthPill}
            </>
          }
          counterpart={
            estimate.customer && (
              <Link
                to={paths.customerDetail({ customerId: estimate.customer.id })}
                className="hover:underline"
              >
                {estimate.customer.name}
              </Link>
            )
          }
          // Two pricing lineages, deliberately kept apart: a draft's own
          // price (its `financials` are NULL by design, spec 05 §8) and the
          // quoted total Tharstern knows. The label says
          // which this is.
          value={quotedPence != null && formatQuotedPrice(quotedPence)}
          valueLabel={
            draftEstimate ? "quoted price, ex VAT" : "quoted total, ex VAT"
          }
          facts={facts}
          // At most two buttons; everything else is in the ⋯ menu.
          actions={
            draftEstimate ? (
              // Withdraw for every open draft, whatever its state (#2309): a
              // request the customer no longer wants must be closable even
              // before a quote arrives. In the header so it is findable. The
              // backend scopes it to the customer's portfolio; a refusal
              // shows under the rail.
              draftEstimate.state !== "withdrawn" &&
              isFeatureEnabled("merchandise_draft_estimates") && (
                <Button
                  variant="outline"
                  onClick={openWithdrawDraftEstimate}
                  disabled={draftEstimateBusy !== null}
                >
                  Withdraw request
                </Button>
              )
            ) : (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <EstimateControls
                  estimate={estimate}
                  onEstimateChanged={refetchEstimate}
                />
              </div>
            )
          }
        />
      </div>

      {/* The rail, in its own card, padded as the job page's stage rail is. */}
      <Card>
        <CardContent className="space-y-3 py-3">
          <LifecycleRail rail={rail} ariaLabel={`Where ${title} is`} />
          {draftLifecycle && (
            <p className="text-sm text-muted-foreground">
              {draftLifecycle.lead}
              {draftLifecycle.ticket && (
                <>
                  {" · "}
                  <Link
                    to={paths.ticketDetail({ id: draftLifecycle.ticket.id })}
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    {draftLifecycle.ticket.label}
                  </Link>
                </>
              )}
              {draftLifecycle.tail && ` · ${draftLifecycle.tail}`}
            </p>
          )}
        </CardContent>
      </Card>

      {draftEstimateError && (
        <p role="alert" className="text-sm text-[color:var(--color-error)]">
          {draftEstimateError}
        </p>
      )}

      <ObjectTabs ariaLabel={title} current={tab ?? ""} tabs={tabs} />

      {tab === null && (
        <Card className="p-12 text-center">
          <h2 className="mb-2 text-lg">Page not found</h2>
          <p className="text-muted-foreground">
            This estimate has no “{rawTab}” tab.{" "}
            <Link to={tabPath("now")} className="underline">
              Open the estimate
            </Link>
            .
          </p>
        </Card>
      )}

      {tab === "now" && (
        <NowPanel
          items={nowItems}
          footer={
            relatedJob && (
              <JobRow
                relatedJob={relatedJob}
                relatedJobInvoice={linkedJobInvoicePresentation(
                  relatedJob.invoiced_at ?? null,
                )}
              />
            )
          }
        />
      )}

      {tab === "lines" && (
        <LinesTab
          estimate={estimate}
          draft={draftEstimate}
          whatWasAskedFor={
            draftEstimate && (
              <WhatWasAskedFor
                draft={draftEstimate}
                description={estimate.description || null}
                unlink={
                  // Unlink lives beside the quote it would remove — and on
                  // this page rather than the Unlinked-quotes lane, which by
                  // definition holds no linked quote. Open to anyone who can
                  // link, which is why the confirm states the consequence
                  // rather than asking "are you sure?".
                  isFeatureEnabled("merchandise_draft_estimates") &&
                  draftEstimate.linkedQuote &&
                  draftEstimate.state !== "withdrawn" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUnlinkConfirmOpen(true)}
                      disabled={draftEstimateBusy !== null}
                    >
                      <Unlink className="size-3.5" aria-hidden />
                      Unlink quote
                    </Button>
                  )
                }
              />
            )
          }
        />
      )}

      {tab === "price" && (
        <div className="space-y-4">
          {draftEstimate && (
            <DraftEstimateCostChangedBanner
              request={draftEstimate}
              busy={draftEstimateBusy}
              onReprice={handleRepriceDraftEstimate}
              onWithdraw={openWithdrawDraftEstimate}
            />
          )}
          {draftEstimate ? (
            <DraftPriceTab draft={draftEstimate} />
          ) : (
            <PriceTab estimate={estimate} />
          )}
        </div>
      )}

      {tab === "delivery" && <DeliveryTab estimate={estimate} />}

      {tab === "billing" && <BillingTab estimate={estimate} />}

      {tab === "details" && (
        <DetailsTab
          estimate={estimate}
          draft={draftEstimate}
          origin={originText}
        />
      )}

      {tab === "activity" && (
        // The backend's notes block is deliberately complete, so it includes
        // the Tharstern description. The line's description is that text's
        // canonical home, so repeating it here just reads as the page saying
        // the same thing twice. A description that has been *edited* since
        // is still worth showing, hence the equality check rather than
        // dropping the kind outright.
        <ActivityTab
          notes={(information.notes ?? []).filter(
            (note) =>
              note.kind !== "tharstern_description" ||
              note.body.trim() !== (estimate.description ?? "").trim(),
          )}
        />
      )}

      {draftEstimate?.linkedQuote && (
        <AlertDialog
          open={unlinkConfirmOpen}
          onOpenChange={(open) => {
            if (draftEstimateBusy !== "unlink") setUnlinkConfirmOpen(open);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Unlink Promo Office quote{" "}
                {draftEstimate.linkedQuote.promoOfficeQuoteNumber}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This removes Promo Office quote{" "}
                {draftEstimate.linkedQuote.promoOfficeQuoteNumber} from{" "}
                {draftEstimate.reference} and clears the price One derived from
                it. The request goes back to Awaiting quote and the quote
                returns to the Unlinked quotes lane.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={draftEstimateBusy === "unlink"}>
                Keep the link
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  void handleUnlinkDraftEstimateQuote();
                }}
                disabled={draftEstimateBusy === "unlink"}
              >
                {draftEstimateBusy === "unlink" ? "Unlinking…" : "Unlink quote"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {draftEstimate && (
        <AlertDialog
          open={withdrawConfirmOpen}
          onOpenChange={(open) => {
            if (draftEstimateBusy !== "withdraw") setWithdrawConfirmOpen(open);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Withdraw this merchandise request?
              </AlertDialogTitle>
              <AlertDialogDescription>
                The request is closed and the merchandise team&apos;s ticket is
                resolved. Nothing has been sent to the customer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-1">
              <label
                htmlFor="withdraw-draft-estimate-reason"
                className="text-sm font-medium"
              >
                Reason (optional)
              </label>
              <Textarea
                id="withdraw-draft-estimate-reason"
                rows={2}
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                placeholder="e.g. customer no longer needs them"
                disabled={draftEstimateBusy === "withdraw"}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={draftEstimateBusy === "withdraw"}>
                Keep the request
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  void handleWithdrawDraftEstimate();
                }}
                disabled={draftEstimateBusy === "withdraw"}
              >
                {draftEstimateBusy === "withdraw" ? "Withdrawing…" : "Withdraw"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
    >
      <ArrowLeft className="w-4 h-4" />
      Back to Estimates
    </button>
  );
}
